import { useCallback } from 'react';

import type { ComposerGif } from '@shared/dream-components';

import type { SearchGifsResponse } from '~/services/api/community/types';

const COMMUNITY_GIFS_PATH = '/community_gifs';

interface SearchGifsOptions {
  limit: number;
  signal: AbortSignal;
}

const isSearchGifsResponse = (value: unknown): value is SearchGifsResponse =>
  typeof value === 'object' && value !== null && Array.isArray((value as { data?: unknown }).data);

const useSearchCommunityGifs = () =>
  useCallback(async (query: string, { limit, signal }: SearchGifsOptions): Promise<ComposerGif[]> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (query.trim()) params.set('q', query.trim());
    const res = await fetch(`${COMMUNITY_GIFS_PATH}?${params}`, { signal });
    if (!res.ok) throw new Error('gif_search_failed');
    const data: unknown = await res.json();
    if (!isSearchGifsResponse(data)) throw new Error('gif_search_invalid');

    const gifs: ComposerGif[] = [];
    for (const gif of data.data) {
      const fullUrl = gif.images.fixed_height?.url ?? gif.images.fixed_height_small?.url;
      if (!fullUrl) continue;
      gifs.push({
        id: gif.id,
        title: gif.title,
        url: fullUrl,
        thumbUrl: gif.images.fixed_height_small?.url ?? gif.images.fixed_height?.url,
      });
    }
    return gifs;
  }, []);

export default useSearchCommunityGifs;
